import { observer } from 'mobx-react-lite'
import { useMemo, useState } from 'react'
import {
  Box,
  Button,
  ContainerHeader,
  Divider,
  Dropdown,
  FullPageWrapper,
  IconButton,
  SpinnerLoader,
  Status,
  Typography,
} from 'shared/ui'
import { type ActionItem } from 'shared/ui/Actions/Actions'
import { layoutStore } from 'shared/layout/Layout/store/LayoutStore'
import { useSettingsContext } from 'pages/settings/context/settingsContext'
import { useWidgetChatContext } from 'pages/settings/pages/widgetChat/context/widgetChatContext'
import { ContentRight } from 'pages/settings/pages/widgetChat/ui/ContentRight'
import { ConversationsBlock } from 'pages/settings/pages/widgetChat/ui/ConversationsBlock'
import { CustomizeBlock } from 'pages/settings/pages/widgetChat/ui/CustomizeBlock'
import { InstallBlock } from 'pages/settings/pages/widgetChat/ui/InstallBlock'

import styles from './styles.module.scss'

const WidgetChatContent = observer(() => {
  const {
    initialLoading,
    websiteChatWidgetSettings,
    openDisableConfirmModal,
    handlePublish,
    loadingAction,
    isActionLoading,
    hasUnsavedChanges,
  } = useWidgetChatContext()
  const { pageLayoutStore } = useSettingsContext()
  const [isPreviewPanelOpen, setIsPreviewPanelOpen] = useState(false)
  const [isDisableDropdownOpen, setIsDisableDropdownOpen] = useState(false)

  const isSmallView = layoutStore.isMobileViewWithoutMSize
  const isActive = websiteChatWidgetSettings?.status ?? false

  const actions: ActionItem[] = useMemo(() => {
    if (isActive) {
      return [
        {
          text: 'Disable chat widget',
          onAction: openDisableConfirmModal,
          buttonProps: {
            contained: 'secondary',
            size: 'medium',
            disabled: isActionLoading,
          },
        },
        {
          text: 'Publish changes',
          onAction: handlePublish,
          buttonProps: {
            contained: 'primary',
            size: 'medium',
            disabled: isActionLoading || !hasUnsavedChanges,
            loading: loadingAction === 'publish',
          },
        },
      ]
    }
    return [
      {
        text: 'Activate',
        onAction: handlePublish,
        buttonProps: {
          contained: 'primary',
          size: 'medium',
          disabled: isActionLoading,
          loading: loadingAction === 'publish',
        },
      },
    ]
  }, [
    isActive,
    openDisableConfirmModal,
    handlePublish,
    isActionLoading,
    loadingAction,
    hasUnsavedChanges,
  ])

  const disableDropdownItems = useMemo(
    () => [
      {
        id: 'disable',
        label: 'Disable chat widget',
        onClick: openDisableConfirmModal,
      },
    ],
    [openDisableConfirmModal]
  )

  const smallViewActions: ActionItem[] = useMemo(() => {
    const previewButton: ActionItem = {
      buttonComponent: (
        <Button
          text='See widget preview'
          size='medium'
          contained='secondary'
          onClick={() => setIsPreviewPanelOpen(true)}
          aria-label='See widget preview'
        />
      ),
      medium: true,
    }
    const publishButton: ActionItem = {
      buttonComponent: (
        <Button
          text={isActive ? 'Publish changes' : 'Activate'}
          size='medium'
          contained='primary'
          onClick={handlePublish}
          disabled={isActionLoading || (isActive && !hasUnsavedChanges)}
          loading={loadingAction === 'publish'}
          aria-label={isActive ? 'Publish changes' : 'Activate'}
        />
      ),
      medium: true,
    }
    if (isActive) {
      return [
        {
          buttonComponent: (
            <Dropdown
              show={isDisableDropdownOpen}
              onClose={() => setIsDisableDropdownOpen(false)}
              triggerComponent={() => (
                <IconButton
                  icon='moreHorizontal'
                  variant='contained'
                  color='secondary'
                  size='medium'
                  ariaLabel='WidgetChat_actions'
                  disabled={isActionLoading}
                  onClick={() => setIsDisableDropdownOpen((prev) => !prev)}
                />
              )}
              items={disableDropdownItems}
              placement='bottom-start'
              ariaLabel='WidgetChat_actions'
            />
          ),
          medium: true,
        },
        previewButton,
        publishButton,
      ]
    }
    return [previewButton, publishButton]
  }, [
    isActive,
    disableDropdownItems,
    isDisableDropdownOpen,
    isActionLoading,
    loadingAction,
    handlePublish,
    hasUnsavedChanges,
  ])

  if (initialLoading) return <SpinnerLoader ariaLabel={'WidgetChat'} />

  return (
    <>
      <ContainerHeader
        customTitle={
          <div className={styles.header}>
            <div className={styles.titleRow}>
              <Typography variant='body-lg-medium' ariaLabel='WidgetChat_title'>
                Website Chat Widget
              </Typography>
              <Status
                title={isActive ? 'Active' : 'Disabled'}
                intent={isActive ? 'positive' : 'neutral'}
                emphasis='high'
                size='medium'
                icon={isActive ? 'check2' : 'minusCircle'}
              />
            </div>
          </div>
        }
        rightTitleContent={null}
        titleIconProps={pageLayoutStore.titleIconProps}
        titleIconAction={pageLayoutStore.handleToggleCollapse}
        actionIconTooltipProps={pageLayoutStore.headerMenuTooltipIconProps}
        isTitleDivider
        actionsProps={
          isSmallView
            ? {
                showItems: isActive ? 3 : 2,
                isStrictShowItems: true,
                actions: smallViewActions,
                isButton: true,
                gap: 8,
              }
            : { isButton: true, gap: 8, actions }
        }
        actionsGap={8}
        paddingLeft={20}
        paddingRight={20}
        paddingTop={24}
        paddingBottom={24}
      />
      <Divider
        sx={{
          borderColor: 'var(--border-neutral-default)',
        }}
      />
      <Box className={styles.contentLayout}>
        <Box className={styles.leftBlock}>
          <CustomizeBlock />
          <Divider
            sx={{
              margin: '32px 0 26px 0',
              borderColor: 'var(--border-neutral-default)',
            }}
          />
          <ConversationsBlock />
          <Divider
            sx={{
              margin: '26px 0',
              borderColor: 'var(--border-neutral-default)',
            }}
          />
          <InstallBlock />
        </Box>
        {!isSmallView && (
          <>
            <Divider
              orientation='vertical'
              sx={{
                borderColor: 'var(--border-neutral-default)',
                height: '100% !important',
                width: '1px',
              }}
            />
            <ContentRight />
          </>
        )}
      </Box>
      {isSmallView && isPreviewPanelOpen && (
        <>
          <div
            className={styles.previewOverlay}
            onClick={() => setIsPreviewPanelOpen(false)}
            aria-hidden
          />
          <div className={styles.previewPanel} aria-label='Widget preview'>
            <div className={styles.previewPanelHeader}>
              <IconButton
                ariaLabel='WidgetChat_close_preview'
                variant='icon'
                icon='close'
                iconProps={{ color: 'var(--content-primary-tertiary)' }}
                onClick={() => setIsPreviewPanelOpen(false)}
              />
            </div>
            <div className={styles.previewPanelContent}>
              <ContentRight />
            </div>
          </div>
        </>
      )}
    </>
  )
})

export const WidgetChat = observer(() => (
  <FullPageWrapper className={styles.page}>
    <WidgetChatContent />
  </FullPageWrapper>
))
